import type { FormBuilderData } from "@/features/forms/api/formBuilderApi";
import EntryCardEntryValues from "@/features/entry-values/components/EntryCardEntryValues";
import { useScreenerCopy } from "@/features/i18n/screenerCopy";
import { FormPublicCover } from "@/features/pages/components/FormPublicCover";
import {
    formDisplayOptions,
    formInstructionsPlacement,
    formTitleAlign,
    trimmedField,
} from "@/features/pages/utils/formChrome";
import ScreenerResult from "@/features/screeners/components/ScreenerResult";
import ScreenerUnavailable from "@/features/screeners/components/ScreenerUnavailable";
import { TrackerChart } from "@patient-watch/charts";
import type { Database, EntryValueViewT, EntryViewT, ScreenerT } from "@patient-watch/types";
import { cn } from "@patient-watch/ui";
import { useMemo, useState, type ReactNode } from "react";

type InputRow = Database["public"]["Tables"]["inputs"]["Row"];

/** Made-up results at four timepoints, as a share of each score's maximum. */
const EXAMPLE_TIMEPOINTS = [
    { label: "Baseline", share: 0.7, daysAgo: 180 },
    { label: "6 weeks", share: 0.55, daysAgo: 138 },
    { label: "3 months", share: 0.4, daysAgo: 90 },
    { label: "6 months", share: 0.3, daysAgo: 0 },
];

function exampleValue(input: InputRow, share: number, index: number): number {
    const max = input.max_score ?? input.max_value ?? 10;
    const min = input.min_value ?? 0;
    // Nudge each score a little so the series do not sit on top of each other.
    const nudged = Math.min(1, Math.max(0, share + (index % 3) * 0.05));
    return Math.round(min + (max - min) * nudged);
}

function exampleEntryValue(form: FormBuilderData, input: InputRow, value: number): EntryValueViewT {
    return {
        input_id: input.input_id,
        input_label: input.input_label,
        input_key: input.input_key ?? "number",
        number_value: value,
        max_score: input.max_score,
        max_value: input.max_value,
        min_value: input.min_value,
        chart_order: input.chart_order,
        is_primary_score: input.is_primary_score,
        form_id: form.form_id,
    } as unknown as EntryValueViewT;
}

/** Calculated scores plus any question already set to show on the chart. */
function scoredInputs(form: FormBuilderData): InputRow[] {
    const hidden = form.hiddenInputs ?? [];
    const charted = form.pages.flatMap((page) =>
        (page.inputs as InputRow[]).filter((input) => input.chart_order != null)
    );
    return [...hidden, ...charted.filter((input) => !hidden.some((item) => item.input_id === input.input_id))];
}

function exampleEntries(form: FormBuilderData): EntryViewT[] {
    const inputs = scoredInputs(form);
    const now = Date.now();
    return EXAMPLE_TIMEPOINTS.map((timepoint, index) => {
        const date = new Date(now - timepoint.daysAgo * 24 * 60 * 60 * 1000).toISOString();
        return {
            entry_id: `example-${index}`,
            form_id: form.form_id,
            timing_label: timepoint.label,
            calculated_entry_time: date,
            entry_time: date,
            entry_values: inputs.map((input, i) =>
                exampleEntryValue(form, input, exampleValue(input, timepoint.share, i))
            ),
        } as unknown as EntryViewT;
    });
}

function PreviewNote({ children }: { children: ReactNode }) {
    return <p className="mb-3 text-2xs text-muted-foreground text-center">{children}</p>;
}

function EmptyPreview({ children }: { children: ReactNode }) {
    return <p className="py-12 text-muted-foreground text-sm text-center text-pretty">{children}</p>;
}

function CalculatorPreview({ form }: { form: FormBuilderData }) {
    const entryValues = useMemo(() => {
        const latest = exampleEntries(form)[0];
        return (latest?.entry_values ?? []).filter((value) =>
            (form.hiddenInputs ?? []).some((input) => input.input_id === value.input_id)
        );
    }, [form]);

    if (entryValues.length === 0) {
        return <EmptyPreview>Calculated scores show here once you add one.</EmptyPreview>;
    }
    return (
        <>
            <PreviewNote>How a clinician sees the scores on one entry. Example values.</PreviewNote>
            <EntryCardEntryValues entryValues={entryValues} variant="plain" defaultExpanded size="sm" />
        </>
    );
}

function ChartPreview({ form }: { form: FormBuilderData }) {
    const tracker = useMemo(
        () =>
            ({
                form_id: form.form_id,
                form: { show_chart: form.show_chart, chart_type: form.chart_type, scoring: form.scoring },
                entries_view: exampleEntries(form),
            }) as unknown as Parameters<typeof TrackerChart>[0]["tracker"],
        [form]
    );

    if (form.show_chart === false) {
        return <EmptyPreview>The chart is off. Clinicians see the answers without a chart.</EmptyPreview>;
    }
    if (!scoredInputs(form).some((input) => input.chart_order != null)) {
        return <EmptyPreview>Turn on a score to plot it.</EmptyPreview>;
    }
    return (
        <>
            <PreviewNote>The tracker chart on a patient&apos;s results. Example values.</PreviewNote>
            <TrackerChart tracker={tracker} compact />
        </>
    );
}

type ScreenerScreen = "eligible" | "ineligible" | "unavailable";

function ScreenerPreview({ screener }: { screener: ScreenerT | null | undefined }) {
    const copy = useScreenerCopy();
    const [screen, setScreen] = useState<ScreenerScreen>("eligible");

    if (!screener) {
        return <EmptyPreview>This form is not a screener.</EmptyPreview>;
    }

    const isEligible = screen === "eligible";
    const title = (isEligible ? screener.eligible_title : screener.ineligible_title)?.trim();
    const body = (isEligible ? screener.eligible_body : screener.ineligible_body)?.trim();

    return (
        <>
            <div className="flex justify-center gap-1 mb-4" role="tablist" aria-label="Screener screens">
                {(
                    [
                        ["eligible", "Eligible"],
                        ["ineligible", "Not eligible"],
                        ["unavailable", "Link closed"],
                    ] as const
                ).map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={screen === value}
                        onClick={() => setScreen(value)}
                        className={cn(
                            "px-2.5 py-1 border rounded-full text-xs transition-colors",
                            screen === value
                                ? "border-teal-500 bg-teal-50 text-foreground dark:bg-teal-950/40"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        {label}
                    </button>
                ))}
            </div>
            <div className="pointer-events-none">
                {screen === "unavailable" ? (
                    <ScreenerUnavailable />
                ) : (
                    <ScreenerResult
                        screenerId={screener.screener_id}
                        outcome={screen}
                        title={title || (isEligible ? copy.eligibleTitle : copy.ineligibleTitle)}
                        body={body || (isEligible ? copy.eligibleBody : copy.ineligibleBody)}
                        signupSearch={{}}
                    />
                )}
            </div>
        </>
    );
}

function CoverPreview({ form }: { form: FormBuilderData }) {
    const numberOfQuestions = form.pages.reduce((count, page) => count + page.inputs.length, 0);
    return (
        <div className="pointer-events-none">
            <FormPublicCover
                name={trimmedField(form.form_name)}
                caption={trimmedField(form.form_caption)}
                kicker={trimmedField(form.form_kicker)}
                copyright={trimmedField(form.copyright_info)}
                instructions={
                    formInstructionsPlacement(form as never) === "cover" ? trimmedField(form.form_instructions) : null
                }
                timeToComplete={form.time_to_complete || null}
                numberOfQuestions={numberOfQuestions > 0 ? numberOfQuestions : null}
                titleAlign={formTitleAlign(form)}
                showMetrics={formDisplayOptions(form).showCoverMetrics}
                startLabel="Start"
                isStarting={false}
                onStart={() => undefined}
            />
        </div>
    );
}

/**
 * The preview pane for the builder steps after Questions: what clinicians and patients see in the app for the
 * part of the form being edited. Questions and Language use the interactive form preview instead.
 */
export function BuilderStepPreview({
    step,
    form,
    screener,
}: {
    step: "calculator" | "chart" | "screener" | "settings";
    form: FormBuilderData;
    screener: ScreenerT | null | undefined;
}) {
    return (
        <div className="flex flex-col bg-background border rounded-xl h-full overflow-hidden">
            <div className="flex items-center gap-2 bg-background px-4 py-2 border-b">
                <span className="bg-red-400 rounded-full w-2.5 h-2.5" />
                <span className="bg-amber-400 rounded-full w-2.5 h-2.5" />
                <span className="bg-green-400 rounded-full w-2.5 h-2.5" />
                <p className="flex-1 pr-10 text-muted-foreground text-xs text-center">Preview</p>
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
                {step === "calculator" ? (
                    <CalculatorPreview form={form} />
                ) : step === "chart" ? (
                    <ChartPreview form={form} />
                ) : step === "screener" ? (
                    <ScreenerPreview screener={screener} />
                ) : (
                    <CoverPreview form={form} />
                )}
            </div>
        </div>
    );
}
